import { View, Text, styled, useTheme } from 'tamagui'
import { useState } from 'react'
import { TIER_CONFIGS } from '@/mocks'
import { TierBadge } from '../../common'
import type { ServerTier } from '@/types'

const Card = styled(View, {
  backgroundColor: '$surfaceRaised', borderRadius: '$borderRadiusXl', borderWidth: 1, borderColor: '$borderDefault', padding: 16, gap: 12,
})

const SectionTitle = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
})

const Label = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground',
})

const SmallMuted = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, color: '$color8',
})

const FieldRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12,
})

const TIER_ORDER: ServerTier[] = ['free', 'spark', 'blaze', 'forge', 'legion']

interface UsageEstimatorProps {
  currentTier: ServerTier
}

export function UsageEstimator({ currentTier }: UsageEstimatorProps) {
  const theme = useTheme()

  const [voiceUsers, setVoiceUsers] = useState(15)
  const [hoursPerDay, setHoursPerDay] = useState(3)
  const [videoCallsPerWeek, setVideoCallsPerWeek] = useState(2)
  const [peoplePerCall, setPeoplePerCall] = useState(5)
  const [avgCallLength, setAvgCallLength] = useState(45)
  const [goLiveHours, setGoLiveHours] = useState(0)
  const [avgViewers, setAvgViewers] = useState(0)

  // Calculations
  const voiceMH = voiceUsers * hoursPerDay * 30
  const videoMH = Math.round((videoCallsPerWeek * 4.3 * peoplePerCall * avgCallLength) / 2)
  const goLiveMH = goLiveHours * 60 * Math.max(avgViewers, 1)
  const totalMH = voiceMH + videoMH + goLiveMH

  // Find recommended tier
  const recommended = TIER_ORDER.find((t) => TIER_CONFIGS[t].mediaHours >= totalMH) || 'legion'
  const recConfig = TIER_CONFIGS[recommended]

  const inputStyle = {
    background: 'transparent',
    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
    borderRadius: 6,
    padding: '6px 10px',
    color: theme.surfaceForeground?.val || '#E2E8F0',
    fontFamily: '$fontMono',
    fontSize: 13,
    outline: 'none',
    width: 80,
    textAlign: 'right' as const,
    boxSizing: 'border-box' as const,
  }

  return (
    <Card>
      <SectionTitle>Usage Estimator</SectionTitle>
      <SmallMuted>Estimate your monthly Media Hours to find the right plan.</SmallMuted>

      <View gap={8}>
        <FieldRow>
          <Label>Active voice users</Label>
          <input type="number" min={0} value={voiceUsers} onChange={(e) => setVoiceUsers(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>Hours per day in voice</Label>
          <input type="number" min={0} value={hoursPerDay} onChange={(e) => setHoursPerDay(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>Video calls per week</Label>
          <input type="number" min={0} value={videoCallsPerWeek} onChange={(e) => setVideoCallsPerWeek(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>People per video call</Label>
          <input type="number" min={0} value={peoplePerCall} onChange={(e) => setPeoplePerCall(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>Avg call length (min)</Label>
          <input type="number" min={0} value={avgCallLength} onChange={(e) => setAvgCallLength(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>Go Live hours/month</Label>
          <input type="number" min={0} value={goLiveHours} onChange={(e) => setGoLiveHours(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
        <FieldRow>
          <Label>Avg viewers per stream</Label>
          <input type="number" min={0} value={avgViewers} onChange={(e) => setAvgViewers(Number(e.target.value) || 0)} style={inputStyle} />
        </FieldRow>
      </View>

      <View
        backgroundColor="$sidebarActive"
        borderRadius="$borderRadiusLg"
        padding={12}
        gap={4}
      >
        <View flexDirection="row" alignItems="center" justifyContent="space-between">
          <Label fontWeight="600">Estimated: {totalMH.toLocaleString()} MH/month</Label>
        </View>
        <View flexDirection="row" alignItems="center" gap={8}>
          <SmallMuted>Recommended:</SmallMuted>
          <TierBadge tier={recommended} size="sm" />
          <SmallMuted>
            {recConfig.label} ({recConfig.mediaHours.toLocaleString()} MH · {recConfig.price === 0 ? 'Free' : `$${recConfig.price}/mo`})
          </SmallMuted>
        </View>
      </View>
    </Card>
  )
}
